import {
    AttendanceDetails,
    AttendanceEvent,
    AttendanceEventType,
    AttendanceStatus,
    AttendanceUserDetails,
    ATTENDANCE_SUCCESS_STATUSES,
    LessonAttendanceStatus,
    LessonDetails,
    StudentAttendanceByLessons,
    StudentLessonActivity
} from "types/attendance"
import {YesNo} from "types/common"
import moment from "moment"
import {Course} from "types/courses"
import {exportCsv, exportExcel, exportPdf} from "./export-table"
import {Permissions} from "@edular/permissions"

import {DailyAttendanceTimeScale} from "types/settings/general-setting"
import {roundTwoDigits} from "./common"
type ExportFileType = "csv" | "excel" | "pdf" | "txt"

export const formatDailyAttendanceHours = (
    hours: number | null | undefined,
    scale: DailyAttendanceTimeScale
): string => {
    const value = hours ?? 0
    if (scale === DailyAttendanceTimeScale.Hours) {
        return `${roundTwoDigits(value)}`
    }
    return `${roundTwoDigits(value * 60)}`
}

type ExportAllLessonsAttendancePayload = {
    course: Course.Course
    items: StudentAttendanceByLessons[]
    lessons: LessonDetails[]
    type: ExportFileType
}

type ExportLessonActivityToCsvPayload = {
    items: StudentLessonActivity[]
    lesson: LessonDetails
    type: ExportFileType
}

type GroupedAttendanceEvents = {
    checkin: AttendanceEvent | null
    checkout: AttendanceEvent | null
    other: AttendanceEvent[]
}

type GetDailyAttendancePermissions = {
    isLocked: boolean
    deletedAt: boolean
    allowsDailyAttendanceManagement: boolean
    currentProfilePermissions: any
}

export const groupAttendanceEvents = (events: AttendanceEvent[]) =>
    events.reduce(
        (acc, event) => {
            if (event.eventType === AttendanceEventType.Checkin) {
                acc.checkin = event
            } else if (event.eventType === AttendanceEventType.Checkout) {
                acc.checkout = event
            } else {
                acc.other.push(event)
            }

            return acc
        },
        {
            checkin: null,
            checkout: null,
            other: []
        } as GroupedAttendanceEvents
    )

export const getUserFullname = (user: AttendanceUserDetails) => [user.firstName, user.lastName].join(" ")

export const getLessonAttendanceStatusFromActivity = (attendance: StudentLessonActivity | AttendanceDetails | null) => {
    if (!attendance?.status || attendance.status === AttendanceStatus.Rejected) {
        return LessonAttendanceStatus.Missed
    }
    if (attendance.status === AttendanceStatus.Irregular) {
        return LessonAttendanceStatus.NeedAction
    }
    if (ATTENDANCE_SUCCESS_STATUSES.includes(attendance.status) && attendance.isVerified === YesNo.Yes) {
        return LessonAttendanceStatus.Verified
    }
    return LessonAttendanceStatus.Attended
}

const createAllLessonsAttendanceTableHead = (lessons: LessonDetails[]) =>
    ["Student ID", "Name"].concat(lessons.map((l) => moment(l.startsAt).format("MM/DD/YYYY")))

const createAllLessonsAttendanceTableData = (items: StudentAttendanceByLessons[]) =>
    items.map((item) => {
        const line = [String(item.student.userId), getUserFullname(item.student)].concat(
            item.items.map(({attendance}) => {
                if (!attendance?.status || attendance.status === AttendanceStatus.Rejected) {
                    return "absent"
                }
                if (attendance.status === AttendanceStatus.Irregular) {
                    return "action needed"
                }
                if (ATTENDANCE_SUCCESS_STATUSES.includes(attendance.status)) {
                    return attendance.isVerified === YesNo.Yes ? "attended/verified" : "attended/not verified"
                }

                return "absent"
            })
        )

        return line
    })

const createAllLessonsAttendanceFilename = (course: Course.Course) =>
    [course.name.replaceAll(" ", "_").toLowerCase(), "attendance"].join("_")

const LESSON_ACTIVITY_TABLE_HEADER = [
    "Student ID",
    "Name",
    "Checkin",
    "Intermediate Events",
    "Checkout",
    "Status",
    "Verified"
]

const createLessonActivityTableBody = (items: StudentLessonActivity[]) =>
    items.map((item) => {
        const line = [String(item.student.userId), getUserFullname(item.student)]
        const {checkin, checkout, other} = groupAttendanceEvents(item.events)
        line.push(checkin?.status ?? "N/A")

        const intermediateEvents = other.map((i) => i.status).join(", ")
        line.push(intermediateEvents ? intermediateEvents : "N/A")

        line.push(checkout?.status ?? "N/A")

        if (!item.status || item.status === AttendanceStatus.Rejected) {
            line.push("absent")
        } else if (item.status === AttendanceStatus.Irregular) {
            line.push("action needed")
        } else {
            line.push("attended")
        }

        const verified = ATTENDANCE_SUCCESS_STATUSES.includes(item.status) ? item.isVerified : "N/A"
        line.push(verified)

        return line
    })

const createLessonActivityFilename = (lesson: LessonDetails) =>
    [lesson.name.replaceAll(" ", "_").toLowerCase(), moment(lesson.startsAt).format("DD_MM_YYYY"), "activity"].join("_")

export const exportAllLessonsAttendanceToFile = ({items, lessons, course, type}: ExportAllLessonsAttendancePayload) => {
    const columns = createAllLessonsAttendanceTableHead(lessons)
    const rows = createAllLessonsAttendanceTableData(items)

    const filename = createAllLessonsAttendanceFilename(course)
    const payload = {
        filename,
        columns,
        rows
    }
    if (type === "csv") {
        exportCsv(payload)
    } else if (type === "excel") {
        exportExcel(payload)
    } else if (type === "pdf") {
        exportPdf(payload)
    }
}

export const exportLessonActivityToFile = ({lesson, items, type}: ExportLessonActivityToCsvPayload) => {
    const rows = createLessonActivityTableBody(items)
    const filename = createLessonActivityFilename(lesson)

    const payload = {
        filename,
        columns: LESSON_ACTIVITY_TABLE_HEADER,
        rows
    }
    if (type === "csv") {
        exportCsv(payload)
    } else if (type === "excel") {
        exportExcel(payload)
    } else if (type === "pdf") {
        exportPdf(payload)
    }
}

export const getDailyAttendancePermissions = (params: GetDailyAttendancePermissions) => {
    const {isLocked, deletedAt, allowsDailyAttendanceManagement, currentProfilePermissions} = params

    const canEdit =
        ((currentProfilePermissions.hasPermissions({
            staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Edit]
        }) &&
            !isLocked) ||
            currentProfilePermissions.hasPermissions({
                staff: [Permissions.Staff.Academics.Registrar.Attendance.LockedAttendance.Edit]
            })) &&
        allowsDailyAttendanceManagement &&
        !deletedAt

    const canDelete =
        ((currentProfilePermissions.hasPermissions({
            staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Delete]
        }) &&
            !isLocked) ||
            currentProfilePermissions.hasPermissions({
                staff: [Permissions.Staff.Academics.Registrar.Attendance.LockedAttendance.Delete]
            })) &&
        allowsDailyAttendanceManagement &&
        !deletedAt

    const canAdd =
        ((currentProfilePermissions.hasPermissions({
            staff: [Permissions.Staff.AcademicAffairs.StudentServices.Attendance.Add]
        }) &&
            !isLocked) ||
            currentProfilePermissions.hasPermissions({
                staff: [Permissions.Staff.Academics.Registrar.Attendance.LockedAttendance.Edit]
            })) &&
        allowsDailyAttendanceManagement &&
        !deletedAt

    return {
        canEdit,
        canAdd,
        canDelete
    }
}
